import type { DebitorProfile } from "@/model/app/debitor-profile"
import type { OrganizationMember } from "@/model/app/organization-member"
import type { OrganizationTeam } from "@/model/app/organization-team"
import type { ChampionshipCategory } from "@/model/championship/championship-category"
import type { Championship } from "@/model/championship/championship"
import type { ChampionshipPricing } from "@/model/championship/championship-pricing"
import type { ChampionshipRegistrationAdditionalCost } from "@/model/championship/championship-registration-additional-cost"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import type { ChampionshipRegistrationTeamChangeRequest } from "@/model/championship/championship-registration-team-change-request"
import type { ChampionshipTeamUploadState } from "@/model/championship/championship-team-upload-state"
import type { ChampionshipUploadFieldType } from "@/model/championship/championship-upload-field-type"
import type { DownPaymentMode } from "@/model/championship/down-payment-mode"
import { RegistrationAction } from "@/model/championship/registration-action"
import type { Invoice } from "@/model/order/invoice"
import type { Payment } from "@/model/order/payment"
import type { MolliePayment } from "@/model/payment/mollie-payment"
import type { PaymentMethod } from "@/model/payment/payment-method"
import { inject, type InjectionKey, type Ref } from "vue"

export type RegistrationActor = "admin" | "customer"
export type RegistrationPermissions = Record<RegistrationActor, Partial<Record<RegistrationAction, boolean>>>

/** The model as returned by `/championship/registration/{organizationId}/accept-event-info/{taskId}` endpoint */
export type ChampionshipRegistrationAcceptEventInfoData = {
    registrationId: string
    championship: Championship
    price: ChampionshipPricing
    permissions: RegistrationPermissions
}

/** The model as returned by `/championship/registration/{organizationId}/select-team/{taskId}` endpoint */
export type ChampionshipRegistrationSelectTeamData = {
    registrationId: string
    categories: ChampionshipCategory[]
    teams: ChampionshipRegistrationTeam[]
    availableTeams: OrganizationTeam[]
    availableMembers: OrganizationMember[]
    availableContactPersons: ChampionshipRegistrationContactPerson[]
    availableDebitorProfiles: DebitorProfile[]
    debitorProfile?: DebitorProfile
    isRegistrationClosed: boolean
    isCheckedIn: boolean
    canManageAdditionalCosts: boolean
    allowSelectTeamAfterRegistrationPeriod: boolean
    permissions: RegistrationPermissions
}

/** The model as returned by `/championship/registration/{organizationId}/approve/{taskId}` endpoint */
export type ChampionshipRegistrationApproveData = {
    registrationId: string
    teams: ChampionshipRegistrationTeam[]
    isRegistrationClosed: boolean
    permissions: RegistrationPermissions
}

/** The model as returned by `/championship/registration/{organizationId}/final-documents/{taskId}` endpoint */
export type ChampionshipRegistrationFinalDocumentsData = {
    registrationId: string
    championshipName: string
    teams: ChampionshipRegistrationTeam[]
    paymentRequests: PaymentRequest[]
    totalAmount: number
    paidAmount: number
    unpaidAmount: number
    hasInvoice: boolean
    invoices: Invoice[]
    isRegistrationClosed: boolean
    permissions: RegistrationPermissions
}

/** The model as returned by `/championship/registration/{organizationId}/pay-order/{taskId}` endpoint */
export type ChampionshipRegistrationPayOrderData = ChampionshipRegistrationSelectTeamData & {
    orderProcessId: string
    totalAmount: number
    paidAmount: number
    unpaidAmount: number
    pendingPayments: MolliePayment[]
    successfulPayments: Payment[]
    hasMolliePayments: boolean
    partialPaymentUnpaid: number
    canProcessTask: boolean
    canCreatePosPayments: boolean
    partialPaymentDueDate: string | undefined
    downPaymentMode: DownPaymentMode
    downPaymentPercent?: number
    downPaymentPerTeamCents?: number
    fullPaymentDueDate: string | undefined
    allowedPaymentMethods: PaymentMethod[]
}

/** The model as returned by `/championship/registration/{organizationId}/finalize-team/{taskId}` endpoint */
export type ChampionshipRegistrationFinalizeTeamData = ChampionshipRegistrationSelectTeamData & {
    /**
     * The deadline for finalizing the team registration.
     */
    deadline: string

    /**
     * Whether the registration is valid to be submitted in the finalize team task.
     */
    registrationValid: boolean

    /**
     * All todos that need to be completed before the team registration can be finalized.
     */
    teamTodos: Record<
        string,
        {
            /**
             * A map of file ID to file info for the given team ID.
             */
            files: Record<string, ChampionshipRegistrationFileInfo>

            /**
             * Whether the team has any open blockers.
             */
            blockers: boolean

            /**
             * Whether the safety champion for this team is valid.
             */
            safetyChampionValid: { valid: boolean; state: "pending" | "ok" | "invalid"; message: string | null }

            /**
             * Whether the accuscore member for this team is valid.
             */
            accuscoreMemberValid: { valid: boolean; state: "pending" | "ok" | "invalid"; message: string | null }

            /**
             * Whether this team's category requires accuscore.
             */
            categoryRequiresAccuscore: boolean
        }
    >

    /**
     * Additional costs per team (admin only). Key is team ID.
     */
    teamAdditionalCosts?: Record<string, ChampionshipRegistrationAdditionalCost[]>

    /** The change requests for each team. */
    changeRequests: ChampionshipRegistrationTeamChangeRequest[]
}

export type ChampionshipRegistrationFileInfo = {
    id: string
    name: string
    type: ChampionshipUploadFieldType
    deadline: string
    state: ChampionshipTeamUploadState
    isUploadable: boolean
    reviewRequired: boolean
    mandatory: boolean
    originalName?: string
    fileSize?: number
    uploadedAt?: string
}

export type ValidationResultBlocker = {
    type: string
    columns: ("participant" | "coach" | "alternate" | "supervisor")[]
    memberId?: string
    message?: string
}

export type ChampionshipRegistrationTeamValidationResult = null | {
    ok: boolean
    blockers: ValidationResultBlocker[]
    availableMembers: {
        organizationMemberId: string
        blockers: ValidationResultBlocker[]
    }[]
}

export type BlankRegistration = {
    numberOfParticipants: number | undefined
    numberOfAlternates: number | undefined
    numberOfEntourages: number | undefined
}

export type ChampionshipRegistrationTeamMember = {
    memberId: string
    type: "participant" | "coach" | "alternate" | "supervisor"
}
export type ChampionshipRegistrationContactPerson = {
    /**
     * The organization member ID of the contact person.
     */
    id?: string
    name?: string
    email?: string
    /** Whether the person is named in a team, i.e. actually registered for the championship */
    registered?: boolean
}

export function validateTeamName(teamName: string | undefined) {
    return (teamName?.length ?? 0) >= 2
}

export const SelectTeamDataInjection = Symbol("SelectTeamData") as InjectionKey<
    Ref<ChampionshipRegistrationSelectTeamData | undefined>
>

export function useChampionshipRegistrationSelectTeamData() {
    const data = inject(SelectTeamDataInjection)

    if (!data) {
        throw new Error("ChampionshipRegistrationSelectTeamData not provided")
    }

    return data
}
